import { useEffect, useRef } from "react";
import { UseInitializeProductsArgs } from "~/hooks/useInitializeProducts";
import useOpportunityProducts from "~/hooks/useOpportunityProducts";
import { useCheckoutStore } from "~/stores/checkout/useCheckoutStore";
import { useBundles } from "./useBundles";
import useCosmeticProducts from "./useCosmeticProducts";
import useSkinBundles from "./useSkinBundles";

type UseInitializeCosmeticProductsArgs = UseInitializeProductsArgs;

const useInitializeCosmeticProducts = ({
  opportunity,
  loadingOpportunity,
  onComplete,
}: UseInitializeCosmeticProductsArgs) => {
  const hasInitialized = useRef(false);
  const lastOpportunityId = useRef<string | null>(null);
  const [selectedProducts, setSelectedProducts] = useCheckoutStore((state) => [
    state.selectedProducts,
    state.setSelectedProducts,
  ]);
  const {
    cosmeticOpportunityProducts,
    isLoading: isOpportunityProductsLoading,
  } = useOpportunityProducts(opportunity);
  const { isLoading: isBundlesLoading } = useBundles();
  const { isLoading: isSkinBundlesLoading } = useSkinBundles();
  const { hasCosmeticProducts } = useCosmeticProducts();

  // Reset initialization when opportunity changes
  useEffect(() => {
    if (opportunity?.id !== lastOpportunityId.current) {
      hasInitialized.current = false;
      lastOpportunityId.current = opportunity?.id || null;
    }
  }, [opportunity?.id]);

  // only run when page is first loaded and no cosmetic products are selected
  useEffect(() => {
    if (
      !hasInitialized.current &&
      !loadingOpportunity &&
      !isOpportunityProductsLoading &&
      !isBundlesLoading &&
      !isSkinBundlesLoading &&
      !hasCosmeticProducts
    ) {
      if (cosmeticOpportunityProducts.length > 0) {
        const result = [...selectedProducts, ...cosmeticOpportunityProducts];
        setSelectedProducts(result);
        hasInitialized.current = true;
        onComplete?.();
      } else {
        // If there are no cosmetic opportunity products, mark as completed immediately
        hasInitialized.current = true;
        onComplete?.();
      }
    }
  }, [
    isOpportunityProductsLoading,
    loadingOpportunity,
    isSkinBundlesLoading,
    isBundlesLoading,
    hasCosmeticProducts,
    cosmeticOpportunityProducts,
    onComplete,
  ]);
};

export default useInitializeCosmeticProducts;
